import React, { createContext, useContext, useEffect, useState } from 'react';
import { ThemeMode } from '../types';

interface ThemeContextType {
  theme: ThemeMode;
  setTheme: (theme: ThemeMode) => void;
  bgClass: string;
  textClass: string;
  accentClass: string;
  surfaceClass: string; // New: for cards, sheets, modals
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export const ThemeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [theme, setTheme] = useState<ThemeMode>('paper');

  // Sync with Tailwind Dark Mode
  useEffect(() => {
    const root = window.document.documentElement;
    if (theme === 'night') {
      root.classList.add('dark');
    } else {
      root.classList.remove('dark');
    }
  }, [theme]);

  // Computed classes based on theme
  const getThemeClasses = (t: ThemeMode) => {
    switch (t) {
      case 'night':
        return { 
          bg: 'bg-night-bg', 
          text: 'text-night-text', 
          accent: 'text-night-accent',
          surface: 'bg-zinc-900 border-zinc-800'
        };
      case 'sepia':
        return { 
          bg: 'bg-sepia-bg', 
          text: 'text-sepia-text', 
          accent: 'text-sepia-accent',
          surface: 'bg-[#eaddcf] border-[#d7c9b8]' // Slightly darker sepia for surfaces
        };
      case 'paper':
      default:
        return { 
          bg: 'bg-paper-bg', 
          text: 'text-paper-text', 
          accent: 'text-paper-accent',
          surface: 'bg-white border-black/5'
        };
    }
  };

  const { bg, text, accent, surface } = getThemeClasses(theme);

  return (
    <ThemeContext.Provider value={{ theme, setTheme, bgClass: bg, textClass: text, accentClass: accent, surfaceClass: surface }}>
      <div className={`min-h-screen transition-colors duration-300 ${bg} ${text}`}>
        {children}
      </div>
    </ThemeContext.Provider>
  );
};

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) throw new Error('useTheme must be used within a ThemeProvider');
  return context;
};